/**
 * The dashboard's paging state.
 *
 * RTK Query (caseListApi.ts) caches the *pages*; this slice holds only where the agent is
 * within them — the token that fetches the current page and the stack of tokens that
 * walked there, so "previous" is a pop rather than a re-count. The list is read-only and
 * carries nothing sensitive, so — unlike the wizard slice — there is no in-memory-only
 * rule here; it is kept a slice purely so paging is one owned, testable place.
 *
 * A continuation token is opaque: this slice stores and replays it without parsing it, the
 * same discipline the transport keeps (caseListApi.ts).
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE_LIMITS } from '../constants/pageLimits';

/** Where the agent is within the paged list. */
export interface CaseListState {
  /** How many cases each page requests. */
  pageSize: number;

  /**
   * The token for the page on screen, or undefined for the first page. This is what the
   * query is keyed on.
   */
  currentToken?: string;

  /**
   * The tokens of the pages walked through to reach the current one. The top is the page
   * before the current; an empty stack means the current page is the first.
   */
  history: string[];
}

const initialState: CaseListState = {
  pageSize: PAGE_SIZE_LIMITS.DEFAULT,
  currentToken: undefined,
  history: [],
};

const caseListSlice = createSlice({
  name: 'caseList',
  initialState,
  reducers: {
    /**
     * Advances to the next page.
     *
     * The current token is pushed onto the history so it can be returned to, and the token
     * CMS handed back for the next page becomes current. Called only when a next token
     * exists, so it always moves forward.
     */
    advancePage(state, action: PayloadAction<string>) {
      state.history.push(state.currentToken ?? '');
      state.currentToken = action.payload;
    },

    /**
     * Returns to the previous page.
     *
     * Pops the token that led to the current page back into place. A pop of the last entry
     * restores the first page (an empty string in the stack reads as "no token"). A no-op
     * when already on the first page.
     */
    retreatPage(state) {
      if (state.history.length === 0) {
        return;
      }

      const previous = state.history.pop();
      state.currentToken = previous === '' ? undefined : previous;
    },

    /** Resets to the first page — used when the list is re-entered. */
    resetPaging(state) {
      state.currentToken = undefined;
      state.history = [];
    },
  },
});

export const { advancePage, retreatPage, resetPaging } = caseListSlice.actions;
export default caseListSlice.reducer;
